import { GoJsProductColors, hasDiagramDarkMode, leftColorStrip, roundedRightRectangle } from '@y42/gojs';
import * as go from 'gojs';
import { OrchestrationNodeType, OrchestrationUINode } from '../../../models/orchestration-diagram.model';
import { orchestrationDiagramConfig } from '../../config/orchestration-diagram.config';
import { OrchestrationDiagramParts } from '../../config/orchestration-part-names';
import { getOrchestrationDerivedNode } from '../../utils/diagram-utils';
import { ContentPanel } from './parts/content-panel';
import { InPort, OutPort } from './parts/ports';
import { nodeToolTip } from './parts/tooltips';
import { contextMenuBinding, nodeDeleteableBinding, nodeLocationBinding, nodeStrokeBinding } from './utils/bindings';
import { mouseHover, mouseLeave } from './utils/events';

const $ = go.GraphObject.make;

export const DefaultNodeTemplate = () =>
  $(
    go.Node,
    go.Node.Spot,
    {
      name: OrchestrationDiagramParts.DefaultNode,
      isShadowed: true,
      shadowColor: orchestrationDiagramConfig.colors.shadowColor,
      shadowOffset: new go.Point(0, 5),
      shadowBlur: 15,
      selectionAdorned: false,
      cursor: 'pointer',
      toolTip: nodeToolTip(),
      mouseHover,
      mouseLeave,
    },
    // Bindings
    nodeLocationBinding(),
    nodeDeleteableBinding(),
    contextMenuBinding(),

    // Parts
    $(
      go.Panel,
      go.Panel.Horizontal,
      InPort(),
      leftColorStrip({ width: 5 }, [colorStripColorBinding()]),
      MainPanel(),
      OutPort(),
    ),
  );

const MainPanel = (): go.Panel =>
  $(
    go.Panel,
    go.Panel.Auto,
    { name: OrchestrationDiagramParts.MainPanel, width: 400 },
    roundedRightRectangle({}, [nodeStrokeBinding(), nodeFillBinding()]),
    ContentPanel(),
  );

const colorStripColorBinding = () =>
  new go.Binding('fill', '', (node: OrchestrationUINode, part: go.Part) => {
    const derivedNode = getOrchestrationDerivedNode(node, part.diagram!);
    const hasDarkMode = hasDiagramDarkMode(part.diagram);

    if (!derivedNode) {
      return orchestrationDiagramConfig.node.colors.deletedSource;
    }

    const OrchestrationNodeTypeToColorStrip: Record<OrchestrationNodeType, string> = {
      [OrchestrationNodeType.Integration]: hasDarkMode
        ? GoJsProductColors.Dark.Integration
        : GoJsProductColors.Light.Integration,
      [OrchestrationNodeType.UiModel]: hasDarkMode ? GoJsProductColors.Dark.Model : GoJsProductColors.Light.Model,
      [OrchestrationNodeType.SqlModel]: hasDarkMode ? GoJsProductColors.Dark.Model : GoJsProductColors.Light.Model,
      [OrchestrationNodeType.Export]: hasDarkMode ? GoJsProductColors.Dark.Export : GoJsProductColors.Light.Export,
    };

    return OrchestrationNodeTypeToColorStrip[derivedNode.nodeType];
  });

const nodeFillBinding = () =>
  new go.Binding('fill', '', (__node: OrchestrationUINode, part: go.Part) => {
    const hasDarkMode = hasDiagramDarkMode(part.diagram);

    return hasDarkMode ? orchestrationDiagramConfig.mainPanel.dark : orchestrationDiagramConfig.mainPanel.light;
  }).ofObject();
